import React, { memo, useEffect, useMemo } from 'react';
import { Card, Segment } from 'tea-component';
import { useIntl } from 'umi';
import { useSafeState, useSetState } from 'ahooks';
import _, { find, get, isEmpty } from 'lodash-es';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import ArrowdownText from '@/pages/intelligence/components/ArrowdownText';
import { ComponentProps } from '@/pages/intelligence/pages/GlobalView/MarketSize/components/MarketEchartsMap';
import { useUrlQueryParam } from '@/pages/intelligence/pages/GlobalView/MarketSize/hooks/useQuery';
import ChartIconType from '@/pages/intelligence/components/ChartsComponents/ChartIconType';
import Loading from '@/pages/intelligence/components/Loading';
import Visualis from '@/pages/intelligence/pages/TopCharts/Companies/components/CompaniesCore/components/Visualis';
import KolTableList from '../../../Kol/details/components/KolTableList';
import { usePcTopchartsStreamingTrend } from '../../hooks';
import { getBarOrPieData, getLineOrPieData } from '../../utils';
import { useOptionslistSegment } from '../../../Kol/hooks/useFilterOptions';

const StreamingGamesEcahrts = ({
  paramsDefault,
  timeConfig,
  userIds: user_ids,
  data = [],
  loading,
  startDateCur,
  endDateCur,
}: Omit<ComponentProps<typeof KolTableList>, 'setParamsDefault'>) => {
  const { formatMessage } = useIntl();
  // echarts切换
  const [chartType, setChartType] = useSafeState<ChartType>(ChartType.Bar);
  // tab切换,持久化，其他组件需要用到这个参数，放到url处
  const [{ subTab = 'hoursWatched' }, setSubTab] = useUrlQueryParam(['subTab']);
  // 图表参数
  const [kolTrendProps, setKolTrendProps] = useSetState<{
    [key in string]: unknown;
  }>({
    order_metric: 'hoursWatched',
    check_metric: 'hoursWatched',
  });

  // 图表数据
  const {
    data: cardList,
    loading: echartsLoading,
    error,
    mutate,
  } = usePcTopchartsStreamingTrend(
    useMemo(
      () => ({
        ...kolTrendProps,
        ..._.omit(paramsDefault, ['check_metric', 'order_metric']),
        ...timeConfig,
        app_ids: user_ids?.userList,
      }),
      [kolTrendProps, user_ids?.userList],
    ),
    chartType,
  );
  const { order_metric, ...restProps } = kolTrendProps || {};
  const [options, hoverBubbleText, titleText] = useOptionslistSegment(
    subTab as string,
    paramsDefault?.order_metric,
  );

  useEffect(() => {
    // TODO: 还没排查到页面刷新，tab高亮值丢失了，先手动重置一下
    setSubTab({ subTab: 'hoursWatched' });
  }, []);

  useEffect(() => {
    // 当传入的userIds为空时,接口refresh处判断了必须要有user_ids才report,不然接口会报错
    // 所以会导致如果传入的为空，将不会发起请求，此处手动设置[]，也减少了一次请求，缓存时长不变
    // 这个接口将会在背后偷偷的report，将会提前返回缓存内容，
    if (_.isEmpty(user_ids?.userList)) {
      mutate([]);
    }
  }, [mutate, user_ids?.userList]);

  //  tab切换事件
  const handleOnClickTabTypeSelect = (type: string) => {
    setSubTab({ subTab: type });
    if (chartType === 'line') {
      setKolTrendProps({
        check_metric: type,
        order_metric: type,
      });
    }
  };

  // 切换charts类型事件
  const handleChartType = (order_metric: ChartType) => {
    setChartType(order_metric);
    if (order_metric === 'line') {
      setKolTrendProps({
        check_metric: subTab,
        order_metric: subTab,
      });
    }
  };
  // 判断数据是否为空
  const isNodata =
    _.isEmpty(user_ids?.userList) &&
    (_.isEmpty(cardList?.metric_time_line) as Boolean);

  // 柱状图以及拼图数据
  const barOrPieData = useMemo(
    () => getBarOrPieData(data?.slice(0, user_ids?.addNumber) || [], subTab),
    [data, user_ids?.addNumber, subTab],
  );
  // 折线图数据
  const lineData = useMemo(
    () => getLineOrPieData(cardList?.metric_time_line || []),
    [cardList],
  );
  // src\pages\intelligence\pages\TopCharts\Companies\components\CompaniesCore\components\Visualis\index.tsx ,整体引入这个组件，与外部业务代码已经解耦，只是把格式转化成对应的，整体不变
  const kolEchartsParams = useMemo(
    () => ({
      topChartsType: 'top_companies_revenue',
      chartType,
      fetchParams: {
        order_metric: 'total_revenue',
        ratio_type: 0,
        granularity: 'daily',
        start_date: startDateCur,
        end_date: endDateCur,
      },
      barOrPieData,
      lineData,
      isError: error,
      isNodata,
      orderTypeInfo: 'desc',
      titleNow: hoverBubbleText,
      imageType: 'mobile',
      isNew: false,
      hoverCloneType: 'pcMetricsTable',
    }),
    [barOrPieData, chartType, error, hoverBubbleText, isNodata, lineData, startDateCur, endDateCur],

  );
  return (
    <>
      <Card className="dbi-mobileGames-card-context">
        <Card.Body
          title={
            <div style={{ display: 'flex' }}>
              <span style={{ marginRight: 10 }}>
                {echartsLoading
                  ? null
                  : formatMessage({ id: 'dbi_Primary_stream_metrics' })}
              </span>
              <ArrowdownText isNew={true} sortedData={titleText} order={paramsDefault?.order} />
            </div>
          }
          operation={
            echartsLoading ? null : (
              <ChartIconType
                chartType={chartType}
                handleChartType={handleChartType}
              />
            )
          }
        >
          {loading || echartsLoading ? (
            <div style={{ height: 396 }}>
              <Loading />
            </div>
          ) : (
            <>
              <div className="dbi-kol-details-change">
                <Segment
                  value={subTab}
                  onChange={handleOnClickTabTypeSelect}
                  options={options}
                />
                {/* <ArrowdownText
                  isNew={true}
                  order={'desc'}
                  sortedData={options[0]?.text}
                /> */}
              </div>
              <div className="char-data">
                <Visualis
                  {...kolEchartsParams}
                />
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </>
  );
};
export default memo(StreamingGamesEcahrts);
